/* eslint-disable react/no-array-index-key */
import * as React from 'react';

import { Bone } from '@lightspeed/flame/Bone';
import { Card, CardSection } from '@lightspeed/flame/Card';
import { Box, Flex } from '@lightspeed/flame/Core';
import { Divider } from '@lightspeed/flame/Divider';
import { Text } from '@lightspeed/flame/Text';

import { toBoolean } from 'root/apps/pages/utils/sanitize';
import SectionTitle from 'root/apps/PurchaseOrder/ShipmentHistory/components/SectionTitle';
import { formatCurrency, formatNumber } from 'root/apps/PurchaseOrder/ShipmentHistory/utils/format';

import type { ShipmentsOverview } from 'root/apps/PurchaseOrder/ShipmentHistory/types';

interface Props {
  isLoading: boolean;
  data: ShipmentsOverview | undefined;
  isMultiCurrency: boolean;
}

const Overview: React.FC<Props> = ({ isLoading, data, isMultiCurrency }) => {
  const { currency_code: currencyCode } = window.merchantos.locale;
  const tdWidth = isMultiCurrency || isLoading ? '25%' : '33.33%';
  const [hasProductCostPermission] = React.useState<boolean | null>(
    toBoolean(window.merchantos.session.sessData?.Rights?.product_cost)
  );

  return (
    <Box style={{ marginBottom: '36px', width: '100%' }}>
      <Flex justifyContent="space-between">
        {!isLoading ? (
          <SectionTitle>Order overview</SectionTitle>
        ) : (
          <Box>
            <Bone height={4} width="150px" />
          </Box>
        )}
        <Card style={{ flex: 2, maxWidth: '60%' }}>
          {isLoading ? (
            <>
              <CardSection>
                <Flex justifyContent="space-between">
                  <Box width={tdWidth} />
                  {new Array(2).fill(0).map((_v, i) => (
                    <Bone key={i} height={3} width={tdWidth} style={{ marginLeft: '5%' }} />
                  ))}
                </Flex>
              </CardSection>
              <Divider />
              {new Array(2).fill(0).map((_v, index, row) => (
                <React.Fragment key={index}>
                  <CardSection style={{ padding: '1rem' }}>
                    <Flex justifyContent="space-between">
                      <Bone height={3} width={tdWidth} />
                      {hasProductCostPermission ? (
                        <>
                          <Bone height={3} width={tdWidth} />
                          <Bone height={3} width={tdWidth} />
                        </>
                      ) : null}
                    </Flex>
                  </CardSection>
                  {index + 1 !== row.length ? <Divider /> : null}
                </React.Fragment>
              ))}
            </>
          ) : (
            <>
              <CardSection>
                <Flex justifyContent="space-between">
                  <Text fontWeight={700} width={`${parseFloat(tdWidth) * 2}%`} textAlign="end">
                    Quantity
                  </Text>
                  {hasProductCostPermission ? (
                    <>
                      {isMultiCurrency ? (
                        <Text width={tdWidth} fontWeight={700} textAlign="end">
                          Subtotal{' '}
                          <span className="notranslate">({data?.currentVendorCurrencyCode})</span>
                        </Text>
                      ) : null}
                      <Text fontWeight={700} width={tdWidth} textAlign="end">
                        Subtotal{' '}
                        {isMultiCurrency ? (
                          <span className="notranslate">({currencyCode})</span>
                        ) : (
                          ''
                        )}
                      </Text>
                    </>
                  ) : null}
                </Flex>
              </CardSection>
              <Divider />
              <CardSection style={{ padding: '1rem' }}>
                <Flex>
                  <Text fontWeight={700} width={tdWidth}>
                    Received
                  </Text>
                  <Text width={tdWidth} textAlign="end" className="notranslate">
                    {data?.sumQtyReceived || data?.sumQtyReceived === 0
                      ? formatNumber(data.sumQtyReceived)
                      : '-'}{' '}
                  </Text>
                  {hasProductCostPermission ? (
                    <>
                      {isMultiCurrency ? (
                        <Text width={tdWidth} textAlign="end" className="notranslate">
                          {data?.sumTotalReceivedVendorCurrency ||
                          data?.sumTotalReceivedVendorCurrency === 0
                            ? formatCurrency(
                                data.sumTotalReceivedVendorCurrency,
                                data.currentVendorCurrencyCode
                              )
                            : '-'}{' '}
                        </Text>
                      ) : null}
                      <Text width={tdWidth} textAlign="end" className="notranslate">
                        {data?.sumTotalReceived || data?.sumTotalReceived === 0
                          ? formatCurrency(data.sumTotalReceived)
                          : '-'}{' '}
                      </Text>
                    </>
                  ) : null}
                </Flex>
              </CardSection>
              <Divider />
              <CardSection style={{ padding: '1rem' }}>
                <Flex>
                  <Text fontWeight={700} width={tdWidth}>
                    Original order
                  </Text>
                  <Text width={tdWidth} textAlign="end" className="notranslate">
                    {data?.sumQtyOrdered || data?.sumQtyOrdered === 0
                      ? formatNumber(data.sumQtyOrdered)
                      : '-'}{' '}
                  </Text>
                  {hasProductCostPermission ? (
                    <>
                      {isMultiCurrency ? (
                        <Text width={tdWidth} textAlign="end" className="notranslate">
                          {data?.sumTotalOrderedVendorCurrency ||
                          data?.sumTotalOrderedVendorCurrency === 0
                            ? formatCurrency(
                                data.sumTotalOrderedVendorCurrency,
                                data.currentVendorCurrencyCode
                              )
                            : '-'}{' '}
                        </Text>
                      ) : null}
                      <Text width={tdWidth} textAlign="end" className="notranslate">
                        {data?.sumTotalOrdered || data?.sumTotalOrdered === 0
                          ? formatCurrency(data.sumTotalOrdered)
                          : '-'}{' '}
                      </Text>
                    </>
                  ) : null}
                </Flex>
              </CardSection>
            </>
          )}
        </Card>
      </Flex>
    </Box>
  );
};

export default Overview;
